import React, { useState, useMemo } from 'react';
import { Resolver, useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import Button from 'components/app-components/ui/button';
import { FieldHint } from 'components/app-components/ui/field-hint';
import { useTheraFernClient } from 'hooks/useFern';
import { formatErrorMessage } from 'utils/error';
import { useUser } from 'context/user';
import theraToast from 'components/generic-components/toast/TheraToast';
import Dropdown from 'components/generic-components/dropdown/Dropdown';
import { ExternalAccount } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';
import { getPaymentRailsForCurrency } from 'lib/bridgeConstants';

interface WalletFormData {
  walletAddress: string;
  stablecoin: string;
  paymentRail: string;
}

const WalletAddMethodModal = ({
  onClose,
  mutateBridgeAccounts,
}: {
  onClose: () => void;
  mutateBridgeAccounts: () => Promise<ExternalAccount[]>;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useUser();
  const { theraFernApi } = useTheraFernClient();
  const {
    register,
    handleSubmit,
    formState: { isValid, isDirty },
    control,
    setValue,
  } = useForm<WalletFormData>({
    resolver: yupResolver(
      yup.object().shape({
        walletAddress: yup.string().required('Wallet address is required'),
        stablecoin: yup.string().required('Stablecoin is required').oneOf(['USDC', 'USDT']),
        paymentRail: yup.string().required('Payment rail is required'),
      }),
    ) as unknown as Resolver<WalletFormData>,
  });

  // Watch stablecoin selection to filter available payment rails
  const selectedStablecoin = useWatch({ control, name: 'stablecoin' });
  const selectedPaymentRail = useWatch({ control, name: 'paymentRail' });

  // Filter payment rails based on selected stablecoin
  // Only show payment rails that support the selected currency
  const availablePaymentRails = useMemo(() => {
    return getPaymentRailsForCurrency(selectedStablecoin);
  }, [selectedStablecoin]);

  // Reset payment rail if current selection is not valid for the new stablecoin
  useMemo(() => {
    if (selectedPaymentRail && selectedStablecoin) {
      const isCurrentRailValid = availablePaymentRails.some((rail) => rail.value === selectedPaymentRail);
      if (!isCurrentRailValid) {
        setValue('paymentRail', '');
      }
    }
  }, [selectedStablecoin, selectedPaymentRail, availablePaymentRails, setValue]);

  const onSubmit = async (data: WalletFormData) => {
    setIsProcessing(true);
    const stablecoin = data.stablecoin.toLowerCase();
    try {
      await theraFernApi.thera.bridge.bridgeApi.createExternalAccount(user?.bridgeUserDetails?.customerId, {
        // @ts-ignore // remove this once the api is updated
        id: uuidv4(),
        walletAddress: data.walletAddress,
        paymentRail: data.paymentRail,
        currency: stablecoin,
      });
      theraToast.success('Wallet added');
      // refresh the bridge external accounts
      await mutateBridgeAccounts();
      onClose();
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to attach wallet');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'wallet-add-error',
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-w-[300px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-5">
          <label className="form-label !flex items-center">
            Wallet Address
            <span className="ml-2 text-sm text-red-500">*</span>
          </label>
          <FieldHint>Wallet address network must be blockchain based, ethereum or others are not supported</FieldHint>
          <input
            type="text"
            className="form-input"
            placeholder="Enter your wallet address"
            {...register('walletAddress')}
          />
        </div>
        <div className="mb-5">
          <label className="form-label required">Stablecoin</label>
          <div className="flex gap-2">
            <label className="flex cursor-pointer items-center">
              <input type="radio" value="USDC" className="hidden" {...register('stablecoin')} />
              <span className="radio-checkmark"></span>
              <span className="text-sm">USDC</span>
            </label>
            <label className="ml-4 flex cursor-pointer items-center">
              <input type="radio" value="USDT" className="hidden" {...register('stablecoin')} />
              <span className="radio-checkmark"></span>
              <span className="text-sm">USDT</span>
            </label>
          </div>
          {selectedStablecoin && (
            <p className="mt-1 text-xs text-neutral-500">
              {availablePaymentRails.length} network{availablePaymentRails.length !== 1 ? 's' : ''} available for{' '}
              {selectedStablecoin}
            </p>
          )}
        </div>
        <div className="mb-5">
          <label className="form-label required">Payment Rail</label>
          <Controller
            name="paymentRail"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                id="paymentRail"
                values={availablePaymentRails}
                fieldDisplayName=""
                name="paymentRail"
                placeholder={selectedStablecoin ? 'Select payment rail' : 'Select stablecoin first'}
                onSelectedItemChange={field.onChange}
                selectedItem={field.value}
                required
                disabled={!selectedStablecoin}
              />
            )}
          />
        </div>
        <div className="flex">
          <Button
            htmlType="submit"
            className="w-full"
            disabled={!isValid || !isDirty || isProcessing}
            loading={isProcessing}
          >
            Add Wallet
          </Button>
        </div>
      </form>
    </div>
  );
};

export default WalletAddMethodModal;
